@page "/roadmap"
@import Badge from "@source-crafter/xzor-ui/Badge.xzor"
@import { type Tone } from "@source-crafter/xzor-ui"

@code {
  readonly phases: { name: string; tone: Tone; items: [string, string][] }[] = [
    {
      name: "Shipped",
      tone: "success",
      items: [
        ["Compiler", "Razor-style .xzor to typed TypeScript classes, with source maps and a tsc check on every build."],
        ["Components", "Every component: the DOM is built once and bindings update only what changed. @if and @switch keep their branch; keyed rows keep their DOM, nested rows and child components across immutable updates."],
        ["Template features", "Keyed loops and loop else, @attr:, class: toggles, aria-* booleans, @bind:input and @bind:debounce, @await, @leave, @once."],
        ["Show and hide", "@show:if and @show:switch hide branches instead of removing them; @pause stops a hidden one."],
        ["Less work", "Cached getters (@Volatile opts out), @async and @cached components, xzor build --minify with a chunk per page."],
        ["Routing", "@page routes, a typed href table, one @Routes block with History API navigation."],
        ["Page shells (PSR)", "xzor build --minify writes one index.html per @page that preloads exactly that page's chunks, plus precompressed copies. In a first test it painted pages 20-39% sooner than a single shell."],
        ["Server apps and endpoints", "A @Run class that implements IServerApplication is a Bun server: it serves the shells and assets from memory (ETag, brotli, gzip) and @Endpoints classes become routes, with typed path, query and body parameters. xzor dev runs it with hot reload."],
        ["Contracts and typed clients", "An interface an endpoint class implements is a contract: a view injects it as Remote<IContract> and the build generates the HTTP client, typed from the contract."],
        ["Dependency injection", "Services wired at build time from TypeScript types; no container at runtime."],
        ["Cascading values and inherited members", "@cascade blocks and @inherits base classes, checked by TypeScript."],
        ["Models and validation", "Trackable models with field state for forms."],
        ["Builds", "Parallel compile, event-driven --watch with hot reload, PWA output."],
        ["Editor", "VS Code: completion, hover, definition, rename, references, live errors."],
        ["xzor-ui", "Liquid glass kit: buttons, sheets, dialogs, menus, tables, stats, forms, icons."],
        ["Kit IntelliSense", "Prop values, page ids and kit classes complete inside attribute values."],
        ["Scoped styles", "A <style> block or X.css.xzor per component, scoped with @scope, with reactive @expr values and @if blocks."],
        ["@module", "Module-level code beside the component: constants, types, helpers and markup values to export."],
      ],
    },
    {
      name: "Now",
      tone: "accent",
      items: [
        ["xzor-ui on npm", "Publish the kit as @source-crafter/xzor-ui, with the same import paths apps use today."],
      ],
    },
    {
      name: "Next",
      tone: "warning",
      items: [
        ["HTML completion", "Element and attribute names in markup, next to the component ones."],
      ],
    },
    {
      name: "Later",
      tone: "neutral",
      items: [["Native target", "The same components rendered natively instead of in the DOM."]],
    },
  ];
}

<section class="page narrow">
  <h1>Roadmap</h1>
  <p class="lead">What xzor does today, what is being built, and what comes after. Plans change; the order is the intent.</p>
  <ol class="roadmap">
    @for (const phase of phases) {
      <li data-tone="@phase.tone">
        <h2><Badge tone="@phase.tone">@phase.name</Badge></h2>
        <dl>
          @for (const [title, text] of phase.items) {
            <dt>@title</dt>
            <dd>@text</dd>
          }
        </dl>
      </li>
    }
  </ol>
</section>

<style>
/* A rail down the left; each phase hangs a dot in its tone on it. */
.roadmap {
  margin: 2rem 0 0;
  padding: 0;
  list-style: none;
}

.roadmap > li {
  position: relative;
  padding: 0 0 2.25rem 2rem;
  border-left: 1px solid var(--xz-line-strong);
}

.roadmap > li:last-child {
  border-left-color: transparent;
}

.roadmap > li::before {
  content: "";
  position: absolute;
  top: 0.3rem;
  left: -0.4rem;
  width: 0.8rem;
  height: 0.8rem;
  border-radius: 50%;
  background: var(--xz-bg);
  box-shadow: inset 0 0 0 2px var(--xz-muted);
}

.roadmap > li[data-tone="success"]::before {
  background: var(--xz-success);
  box-shadow: none;
}

.roadmap > li[data-tone="accent"]::before {
  box-shadow: inset 0 0 0 2px var(--xz-accent), 0 0 0 4px color-mix(in oklab, var(--xz-accent) 22%, transparent);
}

.roadmap h2 {
  margin: 0 0 0.9rem;
  font-size: inherit;
  line-height: 1;
}

.roadmap dl {
  display: grid;
  grid-template-columns: 13rem 1fr;
  gap: 0.6rem 1.5rem;
  margin: 0;
}

.roadmap dt {
  font-weight: 600;
}

.roadmap dd {
  margin: 0;
  color: var(--xz-muted);
}

@media (max-width: 640px) {
  .roadmap dl {
    grid-template-columns: 1fr;
    gap: 0.15rem;
  }

  .roadmap dd + dt {
    margin-top: 0.6rem;
  }
}
</style>
